import React, { useEffect, useRef, useState } from 'react';
import { getActiveRequestCount, subscribeToRequestActivity } from '../utils/requestActivity';

type Phase = 'idle' | 'growing' | 'completing';

/**
 * Thin top-of-page progress bar that shows while one or more REST API
 * requests (via the shared axios instance) are in flight. Driven by a
 * request counter rather than a boolean, so it stays visible for the whole
 * span of overlapping concurrent requests and only hides once the last one
 * settles — not the first.
 */
const GlobalProgressBar: React.FC = () => {
  const [phase, setPhase] = useState<Phase>(getActiveRequestCount() > 0 ? 'growing' : 'idle');
  const cleanupTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const clearPendingTimeout = () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = undefined;
      }
    };

    const unsubscribe = subscribeToRequestActivity((activeCount) => {
      clearPendingTimeout();

      if (activeCount > 0) {
        setPhase('growing');
      } else {
        // Snap to 100% first, then fade the whole bar out, instead of
        // disappearing abruptly the instant the last request settles.
        setPhase('completing');
        cleanupTimeoutRef.current = setTimeout(() => setPhase('idle'), 400);
      }
    });

    return () => {
      unsubscribe();
      clearPendingTimeout();
    };
  }, []);

  if (phase === 'idle') return null;

  const isCompleting = phase === 'completing';

  return (
    <div
      className="fixed left-0 top-0 z-[10000] h-[3px] w-full overflow-hidden bg-transparent transition-opacity duration-300"
      style={{ opacity: isCompleting ? 0 : 1 }}
      role="progressbar"
      aria-label="Loading"
      aria-live="polite"
      aria-busy={!isCompleting}
    >
      <div
        className="h-full ease-out"
        style={{
          width: isCompleting ? '100%' : '90%',
          backgroundColor: '#FDB022',
          boxShadow: '0 0 8px rgba(253, 176, 34, 0.7)',
          transitionProperty: 'width',
          transitionDuration: isCompleting ? '200ms' : '8s',
        }}
      />
    </div>
  );
};

export default GlobalProgressBar;
